class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");

    this.debouncedAutoFilter = debounce(async (event) => {
      let searchParams = this._lastSearchParams;
      if (searchParams instanceof Promise) {
        searchParams = await searchParams;
      }
      if (!searchParams) {
        searchParams = await this.createSearchParams(facetForm);
      }
      this.onSubmitForm(searchParams, event);
    }, 500);

    // This keeps desktop & mobile filters in sync
    // Otherwise, we get incorrect checkbox state info because one will be checked and the other won't
    this.syncFiltersState = (source) => {
      const desktopForm = document.getElementById("FacetFiltersForm");
      const mobileForm = document.getElementById("FacetFiltersFormMobile");
      const forms = [desktopForm, mobileForm];

      // Check the filters that already exist in the URL on page load (In case they came directly to a filtered URL)
      if (source.type === "url") {
        const urlParams = new URLSearchParams(window.location.search);
        forms.forEach((form) => {
          if (!form) return;
          form
            .querySelectorAll('input[type="checkbox"], input[type="radio"]')
            .forEach((input) => {
              const paramValues = urlParams.getAll(input.name);
              const checked = paramValues.includes(input.value);
              input.checked = checked;
              input.setAttribute("aria-checked", checked ? "true" : "false");

              // console.log(
              //   `[Facets][syncFiltersState][url] Updated input: name=${input.name}, value=${input.value}, checked=${checked}`,
              // );
            });
        });
      } else if (source.type === "input" && source.input) {
        // Sync the checked/aria-checked state to ALL matching inputs in BOTH forms for this input
        const changedInput = source.input;
        const checked = source.checked;
        forms.forEach((form) => {
          if (!form) return;
          // Find all matching inputs by name and value
          const selector = `input[name="${changedInput.name}"][value="${changedInput.value}"]`;
          const inputs = form.querySelectorAll(selector);
          inputs.forEach((input) => {
            input.checked = checked;
            input.setAttribute("aria-checked", checked ? "true" : "false");

            // console.log(
            //   `[Facets][syncFiltersState][input] Updated input: name=${input.name}, value=${input.value}, checked=${checked}`,
            // );
          });
        });
      }
    };

    // Run instantly on load as well, just in case
    this.syncFiltersState({ type: "url" });

    // Listen for input changes to keep filter params up to date, but do NOT auto-apply filters
    this._lastSearchParams = this.createSearchParams(facetForm);
    facetForm.addEventListener("input", (event) => {
      if (event.target.type === "checkbox" || event.target.type === "radio") {
        const checked = event.target.checked;
        event.target.setAttribute("aria-checked", checked ? "true" : "false");
        this.syncFiltersState({ type: "input", input: event.target, checked });
        this._lastSearchParams = this.createSearchParams(facetForm);
        // Do NOT auto-apply filters here
      }
      // For price-range, update the stored values but do not apply
      if (event.target.classList.contains("price-range-input")) {
        this._lastSearchParams = this.createSearchParams(facetForm);
      }
    });

    // The apply button (submits the form with current params, including price-range)
    const applyButtons = facetForm.querySelectorAll(".facets__apply");
    applyButtons.forEach((applyButton) => {
      applyButton.addEventListener("click", async (event) => {
        event.preventDefault();
        // No need to manually copy price-range values; ensure inputs have correct names
        let searchParams = await this.createSearchParams(facetForm);
        this.onSubmitForm(searchParams, event);

        // Remove any menu-opening classes
        const menuDrawers = document.querySelectorAll("menu-drawer");
        menuDrawers.forEach((drawer) => {
          drawer.querySelectorAll("details.menu-opening").forEach((details) => {
            details.classList.remove("menu-opening");
          });
        });
      });
    });

    // The reset button (clears all filters)
    const resetButtons = facetForm.querySelectorAll(".facets__reset");
    resetButtons.forEach((resetButton) => {
      resetButton.addEventListener("click", (event) => {
        event.preventDefault();
        // console.log("[Facets] Reset button clicked, clearing all filters");
        // Reset to base URL (no filters)
        const baseUrl = window.location.pathname;
        FacetFiltersForm.renderPage("", event);
      });
    });

    // Enter key support for accessibility
    facetForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      // console.log("[Facets] Form submitted (Enter key or submit event)");
      let searchParams = this._lastSearchParams;
      if (searchParams instanceof Promise) {
        searchParams = await searchParams;
      }
      if (!searchParams) {
        searchParams = await this.createSearchParams(facetForm);
      }
      // console.log("[Facets] Submitting with params:", searchParams);
      this.onSubmitForm(searchParams, event);
    });

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);

    // Accessibility: Listen for open/close on main filter details and update aria-expanded and show/hide text
    const mainDetails = document.querySelector(
      'details[aria-labelledby="filter-toggle-label"]',
    );
    if (mainDetails) {
      const summary = mainDetails.querySelector("summary#filter-toggle-label");
      const showText = summary?.querySelector(".show-text");
      const hideText = summary?.querySelector(".hide-text");
      function toggleAriaAndText() {
        if (mainDetails.open) {
          summary.setAttribute("aria-expanded", "true");
          summary.setAttribute("aria-label", "Close Filters");
          if (showText) {
            showText.style.display = "none";
            showText.setAttribute("aria-hidden", "true");
          }
          if (hideText) {
            hideText.style.display = "";
            hideText.setAttribute("aria-hidden", "false");
          }
        } else {
          summary.setAttribute("aria-expanded", "false");
          summary.setAttribute("aria-label", "Show Filters");
          if (showText) {
            showText.style.display = "";
            showText.setAttribute("aria-hidden", "false");
          }
          if (hideText) {
            hideText.style.display = "none";
            hideText.setAttribute("aria-hidden", "true");
          }
        }
      }
      mainDetails.addEventListener("toggle", toggleAriaAndText);
      // Set initial state
      toggleAriaAndText();
    }

    // Accessibility: Listen for open/close on Sort By dropdown and update aria-expanded dynamically
    document.querySelectorAll(".facet-sort-dropdown").forEach((detailsElem) => {
      const summaryElem = detailsElem.querySelector(
        "summary.facet-filters__label",
      );
      if (summaryElem) {
        function updateSortDropdownAria() {
          summaryElem.setAttribute(
            "aria-expanded",
            detailsElem.open ? "true" : "false",
          );
        }
        detailsElem.addEventListener("toggle", updateSortDropdownAria);
        // Set initial state
        updateSortDropdownAria();
      }
    });

    // Accessibility: Listen for open/close on all filter details and update aria-expanded and aria-label dynamically
    function updateFacetSummaryAria(detailsElem, summaryElem) {
      const label = summaryElem.getAttribute("data-filter-label") || "";
      if (detailsElem.open) {
        summaryElem.setAttribute("aria-expanded", "true");
        summaryElem.setAttribute("aria-label", `Close ${label} filter`);
      } else {
        summaryElem.setAttribute("aria-expanded", "false");
        summaryElem.setAttribute("aria-label", `Open ${label} filter`);
      }
    }
    document.querySelectorAll("details.js-filter").forEach((detailsElem) => {
      const summaryElem = detailsElem.querySelector("summary");
      if (summaryElem) {
        detailsElem.addEventListener("toggle", () => {
          updateFacetSummaryAria(detailsElem, summaryElem);
        });
        // Set initial state
        updateFacetSummaryAria(detailsElem, summaryElem);
      }
    });
    // (aria-checked is now always set in syncFiltersState and on input events)
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const basePath = window.location.pathname;
    // console.log("[Facets] renderPage called with params:", searchParams);
    // console.log("[Facets] Base path:", basePath);
    if (!searchParams) {
      if (basePath.includes("/search")) {
        // console.log(
        //   "renderPage called with empty searchParams on a /search page, aborting.",
        // );
        return;
      }
    }
    FacetFiltersForm.searchParamsPrev = searchParams;

    // Only use min_price/max_price if present in the new searchParams
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop",
    );
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${basePath}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        // Clear cache if new filters are applied (prevents stale cache)
        FacetFiltersForm.filterData = [];
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const found = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!found) {
      console.warn("[Facets] No cached filter data found for this URL.");
      return;
    }
    const html = found.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const countElem = parsed.getElementById("ProductCount");
    if (!countElem) {
      console.warn("[Facets] ProductCount element not found in returned HTML.");
      // Don't block filter application if missing
      return;
    }
    const count = countElem.innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    if (container) {
      container.innerHTML = count;
      container.classList.remove("loading");
    } else {
      console.warn("[Facets] ProductCount container not found in DOM.");
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`,
      ).innerHTML = element.innerHTML;
    });

    const urlParams = new URLSearchParams(window.location.search);
    // Use new param names for price range
    const minPrice = urlParams.get("filter.v.price.gte");
    const maxPrice = urlParams.get("filter.v.price.lte");
    document.querySelectorAll("price-range").forEach((priceRange) => {
      if (minPrice !== null) priceRange.minInput.value = minPrice;
      if (maxPrice !== null) priceRange.maxInput.value = maxPrice;
      priceRange.updateDisplayValues();
      priceRange.updateSliderRange();
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter"),
      );
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    // Ensure that the active facets are not disabled
    FacetFiltersForm.toggleActiveFacets(false);

    // Re-apply the active filters
    const activeFilters = document.querySelectorAll(".js-facet-remove");
    activeFilters.forEach((filter) => {
      filter.classList.remove("disabled");
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById("FacetFiltersFormMobile")
      .closest("menu-drawer")
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    if (searchParams instanceof Promise) {
      searchParams.then((params) => {
        history.pushState(
          { searchParams: params },
          "",
          `${window.location.pathname}${params && "?".concat(params)}`,
        );
      });
      return;
    }
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  // Builder for the active filters
  async createSearchParams(form) {
    // Only include inputs that are actually checked
    const checkedValues = {};
    const allInputs = Array.from(
      form.querySelectorAll('input[type="checkbox"], input[type="radio"]'),
    );
    allInputs.forEach((input) => {
      const checked = input.checked;
      if (checked) {
        if (!checkedValues[input.name]) checkedValues[input.name] = new Set();
        checkedValues[input.name].add(input.value);
      }
    });

    // Build params, filter out any duplicates
    // Duplicates can happen if they loaded in on an already filtered URL
    const params = new URLSearchParams();
    Object.entries(checkedValues).forEach(([name, values]) => {
      values.forEach((value) => {
        params.append(name, value);
      });
    });

    // Add Sort by radio and other inputs (including price-range)
    form
      .querySelectorAll('input:not([type="checkbox"]):not([type="radio"])')
      .forEach((input) => {
        if (input.name && input.value) {
          // Special handling for price-range inputs
          if (input.id === "rangeMin") {
            params.set("filter.v.price.gte", input.value);
          } else if (input.id === "rangeMax") {
            params.set("filter.v.price.lte", input.value);
          } else {
            params.set(input.name, input.value);
          }
        }
      });

    const paramsStr = params.toString();
    console.log("[Facets] createSearchParams URLSearchParams:", paramsStr);
    return paramsStr;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    // This handler is now only used for sort dropdowns or legacy triggers
    event.preventDefault();
    // console.log(
    //   "[Facets] onSubmitHandler called (should only be for sort or legacy)",
    // );
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    const forms = [];
    const isMobile =
      event.target.closest("form").id === "FacetFiltersFormMobile";
    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (
          form.id === "FacetSortForm" ||
          form.id === "FacetFiltersForm" ||
          form.id === "FacetSortDrawerForm"
        ) {
          const noJsElements = document.querySelectorAll(".no-js-list");
          noJsElements.forEach((el) => el.remove());
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === "FacetFiltersFormMobile") {
        forms.push(this.createSearchParams(form));
      }
    });
    // console.log(
    //   "[Facets] Submitting (sort/legacy) with params:",
    //   forms.join("&"),
    // );
    this.onSubmitForm(forms.join("&"), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    // Get all elements
    this.minInput = this.querySelector("input#rangeMin");
    this.maxInput = this.querySelector("input#rangeMax");
    // Ensure correct name attributes for filter submission
    if (this.minInput) this.minInput.name = "min_price";
    if (this.maxInput) this.maxInput.name = "max_price";
    this.minValueDisplay = this.querySelector("#rangeMinValue");
    this.maxValueDisplay = this.querySelector("#rangeMaxValue");
    this.sliderRange = this.querySelector(".slider-range");

    // Remove auto-apply on change; only update display
    // this.minInput.addEventListener("change", this.updateFilters.bind(this));
    // this.maxInput.addEventListener("change", this.updateFilters.bind(this));

    this.minInput.addEventListener("input", this.handleMinInput.bind(this));
    this.maxInput.addEventListener("input", this.handleMaxInput.bind(this));

    this.updateDisplayValues();
    this.updateSliderRange();
  }

  // Handle not letting the min/max sliders pass each other
  handleMinInput() {
    if (parseFloat(this.minInput.value) > parseFloat(this.maxInput.value)) {
      this.minInput.value = this.maxInput.value;
    }
    this.updateDisplayValues();
    this.updateSliderRange();
  }
  handleMaxInput() {
    if (parseFloat(this.maxInput.value) < parseFloat(this.minInput.value)) {
      this.maxInput.value = this.minInput.value;
    }
    this.updateDisplayValues();
    this.updateSliderRange();
  }

  // No longer auto-applies filters; handled by facets__apply
  updateFilters() {
    // Deprecated: do nothing
  }

  // Update the display number values of what the slider value is
  updateDisplayValues() {
    this.minValueDisplay.textContent = this.minInput.value;
    this.maxValueDisplay.textContent = this.maxInput.value;
  }

  updateSliderRange() {
    const min = parseFloat(this.minInput.value);
    const max = parseFloat(this.maxInput.value);
    const minRange = parseFloat(this.minInput.min);
    const maxRange = parseFloat(this.maxInput.max);

    const rangeWidth = ((max - min) / (maxRange - minRange)) * 100;
    const rangeLeft = ((min - minRange) / (maxRange - minRange)) * 100;

    this.sliderRange.style.width = `${rangeWidth}%`;
    this.sliderRange.style.left = `${rangeLeft}%`;
  }

  refreshFilters() {
    // Store the current state of the filters
    const currentState = {
      min_price: this.minInput.value,
      max_price: this.maxInput.value,
    };
    this.applyStoredState(currentState);
    this.updateDisplayValues();
    this.updateSliderRange();
  }

  applyStoredState(state) {
    if (!state) return;
    if (typeof state.min_price !== "undefined")
      this.minInput.value = state.min_price;
    if (typeof state.max_price !== "undefined")
      this.maxInput.value = state.max_price;
    this.updateDisplayValues();
    this.updateSliderRange();
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    if (this.classList.contains("facet-remove-all")) {
      // Clear all active filters
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      const url = window.location.pathname; // Reset to base URL without filters
      FacetFiltersForm.renderPage(url);
    } else {
      // Remove a single filter
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define("facet-remove", FacetRemove);
